import React from 'react'

import Jumbotron from '../components/Jumbotron/Jumbotron'
import ContentContainer from '../components/ContentContainer/ContentContainer'
import Heading, { HeadingLevel, HeadingStyle } from '../components/Heading/Heading'
import Button from '../components/Button/Button'
import FlexBox, { Justification } from '../components/FlexBox/FlexBox'
import InstitutionLogos from '../components/InstitutionLogos/InstitutionLogos'
import TitledContent from '../components/TitledContent/TitledContent'
import FullWidthSection, { PaddingOption } from '../components/FullWidthSection/FullWidthSection'
import Paragraph from '../components/Paragraph/Paragraph'
import MainLayout from '../layouts/MainLayout'
import { COMPANY_NAME } from '../constants'

import jumboImage from './Index/images/home-jumbotron.png'
import genioLogin from './Index/images/genio-login.png'
import genioScreenshots from './Index/images/genio-screenshots.png'
import antSupport from './Index/images/ant-support.png'
import gleanBlue from './Index/images/glean-mobile.png'
import gleanLaptop from './Index/images/glean-laptop.png'

import './Index/Index.scss'
import Card from '../components/Card/Card'
import CardContainer from '../layouts/CardContainer/CardContainer'

const Home: React.FunctionComponent = () => (
  <MainLayout title={COMPANY_NAME}>
    <Jumbotron title="Sonocent Audio Notetaker" image={jumboImage}>
      <Paragraph>
      Welcome to the home of Audio Notetaker - our legacy note taking solution for students and institutions. 
      Please note, Audio Notetaker is no longer available to purchase.
      </Paragraph>
    </Jumbotron>

    <main>
      <CardContainer>
        <Card title="Upgrade to Genio Notes" image={genioScreenshots}>
          <Paragraph>
            Genio Notes is our new cloud-based audio note taking software which empowers students to become better
            learners.
          </Paragraph>
          <Paragraph>
            Genio Notes takes the best of Sonocent Audio Notetaker and delivers a host of new features designed
            to provide students with lifelong study skills.
          </Paragraph>
          <FlexBox justify={Justification.EVENLY} vertical>
            <Button to="https://genio.co/why-upgrade-to-genio/" forGlean>Upgrade Personal Account</Button>
            <Button to="https://genio.co/solutions/institutions" forGlean>Upgrade Institution-wide</Button>
          </FlexBox>
        </Card>
        <Card title="Audio Notetaker Support" image={antSupport}>
          <Paragraph>
            We will stop maintaining Audio Notetaker in August 2026, and officially end support in August 2027.
          </Paragraph>
          <Paragraph>
            If you're still an Audio Notetaker user and need help, then please reach out to our Support Team at support@genio.co.
          </Paragraph>
          <FlexBox justify={Justification.EVENLY} vertical>
            <Button to="https://genio.co/contact/customer-support">Contact Support</Button>
          </FlexBox>
        </Card>
      </CardContainer>

      <FullWidthSection paddingTop={PaddingOption.MEDIUM} paddingBottom={PaddingOption.NONE}>
        <ContentContainer>
          <TitledContent title="Sonocent is now Genio" center>
            <Paragraph className="home__paragraph--left-aligned">
              We originally created Sonocent Audio Notetaker to provide accessible audio notes for people who found working with text notes difficult.
            </Paragraph>

            <Paragraph className="home__paragraph--left-aligned">
              Now, as Genio, we've taken everything we&apos;ve learned from assistive technology and made the learning process more inclusive for students of all abilities.
            </Paragraph>

            <img src={gleanBlue} alt="Cell phone hovering over a notepad illustrating digital transformation" className="home__image--large" />

            <Button to="https://genio.co/" className="home__button--glean" forGlean>Meet Genio</Button>
          </TitledContent>
        </ContentContainer>
      </FullWidthSection>

      <FullWidthSection paddingTop={PaddingOption.MEDIUM} paddingBottom={PaddingOption.NONE}>
        <ContentContainer>
          <Heading headingLevel={HeadingLevel.H2} headingStyle={HeadingStyle.TITLE} className="home__title--centred">
            Why upgrade to Genio Notes
          </Heading>
          <FlexBox justify={Justification.BETWEEN} wrap={false} className="audio-notetaker__admin-tools">
            <FlexBox vertical justify={Justification.START} className="audio-notetaker__admin-tools__content">
              <Paragraph>
                Our intuitive note-taking web application, Genio Notes, makes it easier than ever for students to take control of their own learning through the power of great note taking.
              </Paragraph>
              <Paragraph>
                Beyond note taking, Genio Notes gives students of all abilities a framework for learning that will last a lifetime.
              </Paragraph>
              <Button to="https://genio.co/why-upgrade-to-genio/" forGlean>Personal</Button>
              <Button to="https://genio.co/solutions/institutions" forGlean>Institutions</Button>
            </FlexBox>
            <FlexBox justify={Justification.CENTER} className="audio-notetaker__admin-tools__image-container" vertical>
              <img src={genioLogin} alt="The Genio login screen" />
            </FlexBox>
          </FlexBox>
        </ContentContainer>
      </FullWidthSection>

      <FullWidthSection paddingTop={PaddingOption.MEDIUM} paddingBottom={PaddingOption.SMALL}>
        <ContentContainer>
          <TitledContent title="Here's some of the institutions seeing amazing results with Genio Notes" center>
            <InstitutionLogos />
          </TitledContent>
          <FlexBox justify={Justification.CENTER} vertical className="home__pills-flexbox">
            <Button to="https://genio.co/resources/case-studies-and-user-stories/">View case studies</Button>
          </FlexBox>
        </ContentContainer>
      </FullWidthSection>

      <FullWidthSection paddingTop={PaddingOption.MEDIUM} paddingBottom={PaddingOption.NONE}>
        <ContentContainer>
          <TitledContent title="Discover more about Genio" center>
            <Paragraph>We&apos;re trusted by over 800 institutions worldwide, where thousands of students are using Genio to become better learners.</Paragraph>

            <img src={gleanLaptop} alt="A laptop illustrating studying with Genio Notes" className="home__image--large" />

            <Button to="https://genio.co/resources/case-studies-and-user-stories/" className="home__button--glean" forGlean>
              Hear from our users
            </Button>
          </TitledContent>
        </ContentContainer>
      </FullWidthSection>
    </main>
  </MainLayout>
)

export default Home
